import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  AlertTriangle, 
  Package, 
  ShoppingCart,
  Bell,
  X,
  ExternalLink,
  TrendingDown
} from "lucide-react";

/**
 * AlertScorteBasse - Componente per mostrare alert sui prodotti sotto scorta minima
 * 
 * Props:
 * - token: JWT token per autenticazione
 * - API: Base URL API
 * - variant: 'banner' | 'card' | 'badge' - tipo di visualizzazione
 * - onNavigateToMagazzino: callback per navigare al magazzino
 */
export default function AlertScorteBasse({ 
  token, 
  API, 
  variant = 'banner',
  onNavigateToMagazzino,
  className = ""
}) {
  const [prodottiSottoScorta, setProdottiSottoScorta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (token) {
      fetchProdottiSottoScorta();
    }
  }, [token]);

  const fetchProdottiSottoScorta = async () => {
    try {
      const response = await axios.get(`${API}/prodotti/sotto-scorta`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProdottiSottoScorta(response.data || []);
    } catch (error) {
      // Fallback: carica tutti i prodotti e filtra
      try {
        const allProducts = await axios.get(`${API}/prodotti`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const sottoScorta = (allProducts.data || []).filter(p => {
          const scorta = p.scorta_minima || 5;
          return (p.quantita || 0) <= scorta;
        });
        setProdottiSottoScorta(sottoScorta);
      } catch (e) {
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading || prodottiSottoScorta.length === 0 || dismissed) {
    return null;
  }

  const critici = prodottiSottoScorta.filter(p => (p.quantita || 0) === 0);
  const bassi = prodottiSottoScorta.filter(p => (p.quantita || 0) > 0);

  // Variant: Badge (per navbar o header)
  if (variant === 'badge') {
    return (
      <button
        onClick={() => setDialogOpen(true)}
        className={`relative inline-flex items-center justify-center ${className}`}
        title={`${prodottiSottoScorta.length} prodotti sotto scorta`}
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {prodottiSottoScorta.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {prodottiSottoScorta.length}
          </span>
        )}
      </button>
    );
  }

  // Variant: Banner (per top page)
  if (variant === 'banner') {
    return (
      <>
        <div className={`bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-xl flex items-center justify-between shadow-lg ${className}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-sm">
                ⚠️ {prodottiSottoScorta.length} Prodotti Sotto Scorta
              </p>
              <p className="text-xs text-white/80">
                {critici.length > 0 && `${critici.length} esauriti • `}
                {bassi.length > 0 && `${bassi.length} in esaurimento`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="secondary"
              className="bg-white text-orange-600 hover:bg-orange-50 h-8"
              onClick={() => setDialogOpen(true)}
            >
              <Package className="w-4 h-4 mr-1" />
              Dettagli
            </Button>
            <button 
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Dialog dettagli */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Prodotti Sotto Scorta ({prodottiSottoScorta.length})
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {/* Prodotti esauriti */}
              {critici.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-600 mb-2 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" />
                    ESAURITI ({critici.length})
                  </p>
                  {critici.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg mb-1 border border-red-200">
                      <div>
                        <p className="font-medium text-sm text-slate-800">{p.nome}</p>
                        <p className="text-xs text-slate-500">{p.categoria || 'Prodotto'}</p>
                      </div>
                      <Badge className="bg-red-500 text-white">
                        QTÀ: 0
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Prodotti bassi */}
              {bassi.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-600 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    IN ESAURIMENTO ({bassi.length})
                  </p>
                  {bassi.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg mb-1 border border-amber-200">
                      <div>
                        <p className="font-medium text-sm text-slate-800">{p.nome}</p>
                        <p className="text-xs text-slate-500">
                          Min: {p.scorta_minima || 5} • {p.fornitore || 'Nessun fornitore'}
                        </p>
                      </div>
                      <Badge className="bg-amber-500 text-white">
                        QTÀ: {p.quantita}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2 pt-3 border-t">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Chiudi
              </Button>
              {onNavigateToMagazzino && (
                <Button 
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  onClick={() => {
                    setDialogOpen(false);
                    onNavigateToMagazzino();
                  }}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Vai al Magazzino
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Variant: Card (per dashboard)
  return (
    <Card className={`border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between text-amber-800">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Scorte Basse
          </span>
          <Badge className="bg-amber-500 text-white">
            {prodottiSottoScorta.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {prodottiSottoScorta.slice(0, 3).map(p => (
            <div key={p.id} className="flex items-center justify-between text-sm">
              <span className="truncate flex-1 text-slate-700">{p.nome}</span>
              <Badge 
                variant="outline" 
                className={p.quantita === 0 ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}
              >
                {p.quantita || 0}
              </Badge>
            </div>
          ))}
          {prodottiSottoScorta.length > 3 && (
            <p className="text-xs text-amber-600 text-center">
              +{prodottiSottoScorta.length - 3} altri prodotti
            </p>
          )}
        </div>
        {onNavigateToMagazzino && (
          <Button 
            size="sm" 
            variant="outline"
            className="w-full mt-3 text-amber-700 border-amber-300 hover:bg-amber-100"
            onClick={onNavigateToMagazzino}
          >
            <ExternalLink className="w-3 h-3 mr-2" />
            Vai al Magazzino
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
